import { CalendarIcon } from "@images/box/calendarIcon"
import { BoxProgrammation } from "@modules/boxProgrammation/boxProgrammationTypes"
import { BoxSlotTemplate } from "@modules/boxSlotTemplate/boxSlotTemplateTypes"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { useCallback, useImperativeHandle, useState } from "react"
import { useTheme } from "styled-components"

export type BoxSlotTemplateFormDeleteModalRef = {
  open: () => void
}

type BoxSlotTemplateFormDeleteModalProps = {
  boxSlotTemplate: BoxSlotTemplate
  boxProgrammation: BoxProgrammation
  ref: React.Ref<BoxSlotTemplateFormDeleteModalRef>
}

export function BoxSlotTemplateFormDeleteModal({
  boxSlotTemplate,
  boxProgrammation,
  ref,
}: BoxSlotTemplateFormDeleteModalProps) {
  const [showModal, setShowModal] = useState(false)
  const [isSubmit, setIsSubmit] = useState(false)
  const theme = useTheme()
  const { boxSlotTemplateService } = useServicesContext()
  const toast = useToast()
  const [isSelected, setIsSelected] = useState({
    now: false,
    future: false,
  })
  const { hideSidebar } = useSidebar()

  const onHide = useCallback(() => {
    setShowModal(false)
    setIsSubmit(false)
  }, [])

  const onSubmit = async () => {
    setIsSubmit(true)
    await boxSlotTemplateService
      .deleteBoxSlotTemplate(boxProgrammation.id, boxSlotTemplate.id, isSelected.now)
      .then(() => {
        toast.show({
          severity: "success",
          summary: isSelected.now
            ? word("box.slotTemplates.form.modalDelete.toast.now")
            : word("box.slotTemplates.form.modalDelete.toast.future"),
        })
      })
      .catch((e) => {
        console.error(LoggerType.Box + "delete template slot", e)
        toast.show({
          severity: "error",
          summary: word("box.slotTemplates.form.modalDelete.toast.error"),
        })
      })
      .finally(() => {
        onHide()
        hideSidebar()
      })
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  const dateForTwoWeek = new Date()
  dateForTwoWeek.setDate(dateForTwoWeek.getDate() + 15)

  return (
    <HUDialog visible={showModal} header={null} onHide={onHide} style={{ width: "512px" }}>
      {/* HEADER */}
      <div className="flex flex-column flex-wrap pt-5 gap-2 justify-content-center align-content-center text-center">
        <div className="flex w-full justify-content-center align-content-center">
          <CalendarIcon color={theme.mainWhite} />
        </div>
        <HUText fontStyle="TL" color={theme.mainWhite}>
          {word("box.slotTemplates.form.modal.header.title")}
        </HUText>
        <div className="flex flex-wrap gap-2 justify-content-center align-content-center">
          <HUText fontStyle="LL" color={theme.mainWhite} className="py-2 px-3 rounded-full bg-neutralColor-100">
            {word("box.slotTemplates.form.modal.tag.slotDeletion")}
          </HUText>
        </div>
        <HUText fontStyle="BS" color={theme.neutralColor600}>
          {word("box.slotTemplates.form.modal.header.subtitle")}
        </HUText>
      </div>
      {/* CONTENT */}
      <HUText fontStyle="BM" className="flex mt-5 text-center" color={theme.neutralColor600}>
        {word("box.slotTemplates.form.modalDelete.content.title")}
      </HUText>
      <div className="flex gap-3 mt-3">
        <div
          className={`flex flex-column w-6 p-3 gap-3 cursor-pointer border-1 border-round-2xl ${isSelected.now ? "border-electricPurple-400" : "cursor-pointer border-neutralColor-700 "}`}
          onClick={() => setIsSelected({ now: true, future: false })}
        >
          <HUText fontStyle="TS" className="text-center" color={theme.mainWhite}>
            {word("box.slotTemplates.form.modalDelete.content.now")}
          </HUText>
          <HUText fontStyle="BS" className="line-height-3 text-center" color={theme.neutralColor300}>
            {word("box.slotTemplates.form.modal.content.athele")}
            <br />
            {word("box.slotTemplates.form.modal.content.notification")}
            <br />
            {word("box.slotTemplates.form.modal.content.email")}
            <br />
            {word("box.slotTemplates.form.modal.content.refund")}
          </HUText>
        </div>
        <div
          className={`flex flex-column w-6 p-3 gap-3 cursor-pointer border-1 border-round-2xl ${isSelected.future ? "border-electricPurple-400" : "cursor-pointer border-neutralColor-700"}`}
          onClick={() => setIsSelected({ now: false, future: true })}
        >
          <HUText fontStyle="TS" className="  text-center" color={theme.mainWhite}>
            {word("box.slotTemplates.form.modalDelete.content.future")}
          </HUText>
          <HUText fontStyle="BS" className=" text-center" color={theme.neutralColor300}>
            {` ${word("box.slotTemplates.form.modal.content.starting")} ${formatDateWithHyphen(dayjs(dateForTwoWeek).format("L"))}`}
          </HUText>
        </div>
      </div>
      {/* BUTTON */}
      <div className=" mt-5 flex flex-row gap-2 align-items-center justify-content-center">
        <HUButton
          type="Left_Icon"
          size="M"
          colorType="Quaternary"
          text={word("global.cancel")}
          onClick={() => onHide()}
          className="w-full"
        />
        <HUButton
          type="Right_Icon"
          size="M"
          colorType="Primary"
          text={word("global.check")}
          onClick={async () => await onSubmit()}
          disabled={(!isSelected.future && !isSelected.now) || isSubmit}
          className="w-full"
        />
      </div>
    </HUDialog>
  )
}
